import React, { useContext, useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AgentContext } from '../../context/AgentContext';
import { useLanguage } from '../../context/LanguageContext';
import { createAgent } from '../../services/agentService';
import {
  Scale,
  Calculator,
  FileText,
  HelpCircle,
  Building2,
  FileSpreadsheet,
  FileCheck2,
  ArrowUpRight,
  ArrowUp,
  Loader2,
  Paperclip,
  X,
} from 'lucide-react';

export default function QuickActions() {
  const { agents, refreshAgents } = useContext(AgentContext);
  const { isEnglish } = useLanguage();
  const navigate = useNavigate();
  const [loadingTopic, setLoadingTopic] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const chatInputRef = useRef(null);
  const fileInputRef = useRef(null);
  const dragCounterRef = useRef(0);

  const ACCEPTED_TYPES = [
    'application/pdf',
    'image/png', 'image/jpeg', 'image/webp',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel',
  ];
  const ACCEPTED_EXTENSIONS = ['.pdf', '.png', '.jpg', '.jpeg', '.webp', '.docx', '.doc', '.xlsx', '.xls'];

  const isFileAccepted = (file) => {
    if (ACCEPTED_TYPES.includes(file.type)) return true;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    return ACCEPTED_EXTENSIONS.includes(ext);
  };

  const addPendingFiles = (newFiles) => {
    if (!newFiles || newFiles.length === 0) return;
    const accepted = Array.from(newFiles).filter(isFileAccepted);
    if (accepted.length === 0) return;
    setPendingFiles((prev) => {
      const existingKeys = new Set(prev.map(f => `${f.name}-${f.size}`));
      const toAdd = accepted.filter(f => !existingKeys.has(`${f.name}-${f.size}`));
      return [...prev, ...toAdd].slice(0, 10);
    });
  };

  const removePendingFile = (indexToRemove) => {
    setPendingFiles((prev) => prev.filter((_, idx) => idx !== indexToRemove));
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current += 1;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current -= 1;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    dragCounterRef.current = 0;
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      addPendingFiles(e.dataTransfer.files);
    }
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const pasted = [];
    for (const item of items) {
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file && isFileAccepted(file)) {
          pasted.push(file);
        }
      }
    }
    if (pasted.length > 0) {
      addPendingFiles(pasted);
    }
  };

  const handleChatSubmit = async (customText = null) => {
    const text = (typeof customText === 'string' ? customText : chatInput).trim();
    if ((!text && pendingFiles.length === 0) || chatLoading) return;
    setChatLoading(true);
    try {
      const filesToSend = [...pendingFiles];
      sessionStorage.setItem("tacia_pending_prompt", text);
      sessionStorage.setItem("tacia_auto_send", "true");

      const title = text
        ? (text.length > 35 ? text.substring(0, 35) + "..." : text)
        : (filesToSend.length > 0
            ? `${isEnglish ? 'Analysis' : 'Analisis'}: ${filesToSend[0].name}`
            : (isEnglish ? "Tax Consultation" : "Konsultasi Pajak"));

      const newAgent = {
        agentName: title,
        agentPersonality: "Professional",
        agentPrompt: isEnglish
          ? "You are TACIA, an expert AI tax consultant..."
          : "Kamu asisten AI perpajakan...",
        businessDescription: "",
      };
      const res = await createAgent(newAgent);
      const newAgentId = res?.id;
      await refreshAgents();

      const targetAgentId = newAgentId || (agents && agents.length > 0 ? agents[0].id : null);
      if (targetAgentId) {
        navigate(`/agents/${targetAgentId}`, {
          state: {
            initialPrompt: text,
            initialFiles: filesToSend,
            autoSend: true
          }
        });
      }
    } catch (err) {
      console.error("Gagal membuat sesi konsultasi:", err);
      if (agents && agents.length > 0) {
        navigate(`/agents/${agents[0].id}`, {
          state: {
            initialPrompt: text,
            initialFiles: pendingFiles,
            autoSend: true
          }
        });
      }
    } finally {
      setChatLoading(false);
    }
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleChatSubmit();
    }
  };

  useEffect(() => {
    if (chatInputRef.current) {
      chatInputRef.current.style.height = "auto";
      const scrollH = chatInputRef.current.scrollHeight;
      const nextHeight = Math.min(Math.max(scrollH, 58), 180);
      chatInputRef.current.style.height = `${nextHeight}px`;
      chatInputRef.current.style.overflowY = scrollH > 180 ? "auto" : "hidden";
    }
  }, [chatInput]);

  const featuredDraftingTopic = {
    icon: FileText,
    title: isEnglish ? 'Draft Tax Legal Documents (Word)' : 'Draft Dokumen Perpajakan (Word)',
    desc: isEnglish
      ? 'Generate official tax legal drafts (.docx) formatted to DGT & Tax Court standards.'
      : 'Susun draf surat resmi (.docx) sesuai standar format DJP & Pengadilan Pajak.',
    templatePrompt: isEnglish
      ? 'Hello Tacia, please help me draft an official Tax Objection Letter (Surat Keberatan) for a tax assessment (SKPKB/SKPLB) in Word format.'
      : 'Halo Tacia, bantu saya menyusun draf resmi Surat Keberatan atas Surat Ketetapan Pajak (SKPKB/SKPLB) dalam format Word.',
    subPrompts: [
      {
        label: isEnglish ? 'Tax Objection Letter' : 'Surat Keberatan',
        prompt: isEnglish
          ? 'Hello Tacia, please help me draft an official Tax Objection Letter (Surat Keberatan) for a tax assessment (SKPKB/SKPLB) in Word format.'
          : 'Halo Tacia, bantu saya menyusun draf resmi Surat Keberatan atas Surat Ketetapan Pajak (SKPKB/SKPLB) dalam format Word.',
      },
      {
        label: isEnglish ? 'SPHP Audit Response' : 'Tanggapan SPHP',
        prompt: isEnglish
          ? 'Hello Tacia, please help me draft an official Written Response to SPHP (Tax Audit Findings Notification) in Word format.'
          : 'Halo Tacia, bantu saya menyusun draf resmi Surat Tanggapan atas SPHP (Surat Pemberitahuan Hasil Pemeriksaan) dalam format Word.',
      },
      {
        label: isEnglish ? 'SP2DK Clarification' : 'Tanggapan SP2DK',
        prompt: isEnglish
          ? 'Hello Tacia, please help me draft an official Clarification / Response Letter to SP2DK in Word format.'
          : 'Halo Tacia, bantu saya menyusun draf resmi Surat Tanggapan / Klarifikasi atas SP2DK dalam format Word.',
      },
      {
        label: isEnglish ? 'Penalty Reduction' : 'Pengurangan Sanksi',
        prompt: isEnglish
          ? 'Hello Tacia, please help me draft an official Request for Reduction or Waiver of Administrative Sanctions (Article 36 paragraph 1a KUP Law) in Word format.'
          : 'Halo Tacia, bantu saya menyusun draf resmi Surat Permohonan Pengurangan atau Penghapusan Sanksi Administrasi (Pasal 36 ayat 1a UU KUP) dalam format Word.',
      },
      {
        label: isEnglish ? 'STP Cancellation' : 'Pembatalan STP',
        prompt: isEnglish
          ? 'Hello Tacia, please help me draft an official Request for Reduction or Cancellation of an Incorrect Tax Collection Letter / STP (Article 36 paragraph 1c KUP Law) in Word format.'
          : 'Halo Tacia, bantu saya menyusun draf resmi Surat Permohonan Pengurangan atau Pembatalan STP yang Tidak Benar (Pasal 36 ayat 1c UU KUP) dalam format Word.',
      },
      {
        label: isEnglish ? 'Special Power of Attorney (Surat Kuasa Khusus)' : 'Surat Kuasa Khusus',
        prompt: isEnglish
          ? 'Hello Tacia, please help me draft an official Tax Special Power of Attorney (Surat Kuasa Khusus) in accordance with PMK 44 of 2026 in Word format.'
          : 'Halo Tacia, bantu saya menyusun draf resmi Surat Kuasa Khusus perpajakan sesuai PMK 44 Tahun 2026 dalam format Word.',
      },
    ],
  };

  const consultTopics = [
    {
      icon: FileSpreadsheet,
      title: isEnglish ? 'Coretax Withholding Slips (Excel → XML)' : 'Bukti Potong Coretax (Excel → XML)',
      desc: isEnglish
        ? 'Convert Excel templates into Coretax Unification & PPh 21/26 XML files (BPA1, BPA2, BP21, BP26, BPMP).'
        : 'Konversi template Excel ke XML Bupot Unifikasi & PPh 21/26 (BPA1, BPA2, BP21, BP26, BPMP).',
      templatePrompt: isEnglish
        ? 'Hello Tacia, I want to process and generate Coretax DGT Withholding Tax (Bupot) XML files from the official Excel template. Please help verify the data and generate the XML.'
        : 'Halo Tacia, saya ingin memproses dan membuat file XML Bukti Pemotongan (Bupot) untuk Coretax DJP dari file Excel template resmi. Tolong bantu verifikasi data dan generate XML-nya.',
    },
    {
      icon: Building2,
      title: isEnglish ? 'Corporate Annual Tax Return (Coretax XML)' : 'SPT Tahunan Badan (XML Coretax)',
      desc: isEnglish
        ? 'Generate XML Appendices 3B, 9, 10A, 11A (Promotion, Credits, Receivables, Depreciation) & Fiscal Reconciliation.'
        : 'Generate XML Lampiran 3B, 9, 10A, 11A (Promosi, Kredit, Piutang, Penyusutan) & Rekonsiliasi Fiskal.',
      templatePrompt: isEnglish
        ? 'Hello Tacia, I want to generate Corporate Annual Income Tax Return XML Appendices for Coretax (Appendix 3B / 9 / 10A / 11A). Please guide me through the process.'
        : 'Halo Tacia, saya ingin membuat file XML Lampiran SPT Tahunan PPh Badan Coretax (Lampiran 3B / 9 / 10A / 11A). Tolong pandu prosesnya.',
    },
    {
      icon: FileCheck2,
      title: isEnglish ? 'Audit Review: SPHP, Minutes & SKPKB' : 'Audit Berkas SPHP, Risalah & SKPKB',
      desc: isEnglish
        ? 'Upload SPHP, Closing Conference Minutes, or SKPKB PDFs to extract audit corrections & verify math.'
        : 'Unggah PDF SPHP, Risalah Pembahasan, atau SKPKB untuk ekstraksi pos koreksi & audit matematis.',
      templatePrompt: isEnglish
        ? 'Hello Tacia, I want to upload SPHP / Audit Minutes / SKPKB documents to analyze the audit corrections and verify the mathematical calculations.'
        : 'Halo Tacia, saya ingin mengunggah berkas SPHP / Risalah Pembahasan / SKPKB untuk dianalisis pos koreksinya dan dicek keakuratan perhitungannya.',
    },
    {
      icon: Scale,
      title: isEnglish ? 'Tax Regulation & Legal Research' : 'Konsultasi Regulasi & Riset Pajak',
      desc: isEnglish
        ? 'Q&A on HPP Law, PMK, latest DGT regulations, tax rates, and statutory hierarchy analysis.'
        : 'Tanya jawab UU HPP, PMK, PER DJP terbaru, tarif pajak, dan analisis hierarki peraturan.',
      templatePrompt: isEnglish
        ? 'Hello Tacia, I would like to consult on prevailing Indonesian tax regulations regarding corporate tax obligations.'
        : 'Halo Tacia, saya ingin berkonsultasi mengenai ketentuan regulasi perpajakan yang berlaku terkait kewajiban pajak perusahaan.',
    },
    {
      icon: Calculator,
      title: isEnglish ? 'PPh 21 Simulation & Calculation' : 'Simulasi & Perhitungan PPh 21',
      desc: isEnglish
        ? 'Calculate monthly TER effective rates, Article 17 progressive rates, and Gross-Up tax allowance schemes.'
        : 'Kalkulasi tarif efektif TER bulanan, tarif progresif Pasal 17, dan skema Gross-Up tunjangan.',
      templatePrompt: isEnglish
        ? 'Hello Tacia, please help me create a PPh 21 calculation simulation using the Effective Average Rate (TER) scheme and the Gross-Up tax allowance method.'
        : 'Halo Tacia, tolong bantu saya membuat simulasi perhitungan PPh 21 menggunakan skema tarif efektif (TER) dan metode Gross-Up tunjangan pajak.',
    },
    {
      icon: HelpCircle,
      title: isEnglish ? 'CoreTax DGT Guide & Troubleshooting' : 'Panduan & Solusi CoreTax DJP',
      desc: isEnglish
        ? 'Guidance on Coretax features, tax deposits, 16-digit NPWP validation, tax object codes, and XML import fixes.'
        : 'Bimbingan fitur Coretax, deposit pajak, validasi NPWP 16 digit, kode objek, dan solusi error XML.',
      templatePrompt: isEnglish
        ? 'Hello Tacia, I need guidance on the DGT Coretax system, creating tax deposits, and resolving XML withholding slip import issues.'
        : 'Halo Tacia, saya butuh panduan mengenai sistem Coretax DJP, pembuatan deposit pajak, dan pemecahan kendala impor XML bukti potong.',
    },
  ];

  const handleTopicClick = async (topic) => {
    if (loadingTopic) return;
    setLoadingTopic(topic.title);
    try {
      sessionStorage.setItem("tacia_pending_prompt", topic.templatePrompt);

      const newAgent = {
        agentName: topic.title,
        agentPersonality: "Professional",
        agentPrompt: isEnglish
          ? "You are TACIA, an expert AI tax consultant..."
          : "Kamu asisten AI perpajakan...",
        businessDescription: "",
      };
      const res = await createAgent(newAgent);
      const newAgentId = res?.id;
      await refreshAgents();

      if (newAgentId) {
        navigate(`/agents/${newAgentId}`, {
          state: { initialPrompt: topic.templatePrompt }
        });
      } else if (agents && agents.length > 0) {
        navigate(`/agents/${agents[0].id}`, {
          state: { initialPrompt: topic.templatePrompt }
        });
      }
    } catch (err) {
      console.error("Gagal membuat sesi konsultasi:", err);
      if (agents && agents.length > 0) {
        navigate(`/agents/${agents[0].id}`, {
          state: { initialPrompt: topic.templatePrompt }
        });
      } else {
        alert(isEnglish ? "Failed to start a new session. Please try again." : "Gagal memulai sesi baru. Silakan coba lagi.");
      }
    } finally {
      setLoadingTopic(null);
    }
  };

  return (
    <div>
      {/* ── CLEAN TYPOGRAPHIC HEADER ── */}
      <div className="pt-2 sm:pt-4 pb-5">
        <h2 className="text-2xl sm:text-[26px] font-semibold text-text-pri tracking-tight">
          {isEnglish ? 'What would you like to consult on?' : 'Apa yang ingin Anda konsultasikan?'}
        </h2>
        <p className="text-xs sm:text-sm text-text-sec mt-1">
          {isEnglish
            ? 'Ask about tax regulations, audit assessment files, or draft official legal letters (.docx).'
            : 'Tanyakan regulasi perpajakan, audit berkas pemeriksaan, atau susun draf surat resmi (.docx).'}
        </p>
      </div>

      {/* ── CLEAN PROMPT COMPOSER ── */}
      <div className="mb-7">
        <div
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          className={`
            relative flex flex-col rounded-2xl
            bg-card-bg border
            shadow-[0_4px_20px_rgba(15,23,42,0.04)]
            focus-within:border-text-sec/50
            transition-colors duration-150
            ${isDragging ? 'border-text-pri bg-surface-inset' : 'border-ledger-border'}
          `}
        >
          {/* Drop overlay */}
          {isDragging && (
            <div className="absolute inset-0 z-30 flex flex-col items-center justify-center bg-card-bg/95 rounded-2xl border border-dashed border-text-sec pointer-events-none">
              <Paperclip className="w-5 h-5 text-text-pri mb-1.5" />
              <p className="text-xs font-medium text-text-pri">
                {isEnglish ? 'Drop files here' : 'Lepaskan berkas di sini'}
              </p>
              <p className="text-[11px] text-text-sec mt-0.5">PDF, Word, Excel, JPG, PNG</p>
            </div>
          )}

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept=".pdf,.png,.jpg,.jpeg,.webp,.docx,.doc,.xlsx,.xls"
            className="hidden"
            onChange={(e) => {
              if (e.target.files && e.target.files.length > 0) {
                addPendingFiles(e.target.files);
              }
              e.target.value = "";
            }}
          />

          {/* Textarea */}
          <div className="px-4 pt-3.5 pb-1.5">
            <textarea
              ref={chatInputRef}
              rows={2}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              onKeyDown={handleChatKeyDown}
              onPaste={handlePaste}
              placeholder={
                isEnglish
                  ? 'Type your tax question or attach documents...'
                  : 'Ketik pertanyaan atau lampirkan berkas...'
              }
              disabled={chatLoading}
              className="
                w-full bg-transparent
                focus:outline-none
                resize-none
                text-[14px] sm:text-[14.5px] leading-relaxed text-text-pri
                placeholder:text-text-sec/60
                disabled:opacity-50 disabled:cursor-not-allowed
              "
              style={{ minHeight: '58px', maxHeight: '180px' }}
            />
          </div>

          {/* Attached Files Preview Strip */}
          {pendingFiles.length > 0 && (
            <div className="px-4 pb-2 pt-1.5">
              <div className="flex items-center justify-between mb-1.5 text-[11px] text-text-sec">
                <span className="flex items-center gap-1 font-medium text-text-pri">
                  <Paperclip size={11} />
                  {pendingFiles.length} {isEnglish ? 'files attached' : 'berkas terlampir'}
                </span>
                <button
                  type="button"
                  onClick={() => setPendingFiles([])}
                  className="text-text-sec hover:text-text-pri transition-colors"
                >
                  {isEnglish ? 'Remove all' : 'Hapus semua'}
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-28 overflow-y-auto">
                {pendingFiles.map((f, idx) => (
                  <div
                    key={`${f.name}-${idx}`}
                    className="flex items-center gap-1.5 px-2.5 py-1 bg-surface-inset rounded-lg text-xs text-text-pri"
                  >
                    <Paperclip size={11} className="text-text-sec shrink-0" />
                    <span className="truncate max-w-[180px]">{f.name}</span>
                    <span className="text-text-sec text-[10px]">({(f.size / 1024).toFixed(0)} KB)</span>
                    <button
                      type="button"
                      onClick={() => removePendingFile(idx)}
                      className="p-0.5 hover:bg-surface-hover rounded transition-colors"
                      title={isEnglish ? 'Remove file' : 'Hapus file'}
                    >
                      <X size={11} className="text-text-sec hover:text-text-pri" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bottom Composer Toolbar */}
          <div className="flex items-center justify-between px-3.5 pb-3 pt-1">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={chatLoading}
              title={
                isEnglish
                  ? 'Attach documents or images (PDF, DOCX, XLSX, PNG, JPG)'
                  : 'Lampirkan dokumen atau gambar (PDF, DOCX, XLSX, PNG, JPG)'
              }
              className="
                inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg
                text-xs font-medium text-text-sec
                hover:bg-surface-inset hover:text-text-pri
                transition-colors
              "
            >
              <Paperclip size={13} />
              <span>{isEnglish ? 'Attach files' : 'Lampirkan berkas'}</span>
            </button>

            <button
              onClick={() => handleChatSubmit()}
              disabled={(!chatInput.trim() && pendingFiles.length === 0) || chatLoading}
              title={isEnglish ? 'Send (Enter)' : 'Kirim (Enter)'}
              className="
                flex items-center justify-center w-8 h-8 rounded-lg
                bg-text-pri text-paper
                hover:opacity-90 active:scale-95
                disabled:bg-surface-inset disabled:text-muted disabled:cursor-not-allowed
                transition-all duration-150 shrink-0
              "
            >
              {chatLoading ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <ArrowUp size={15} strokeWidth={2.25} />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* ── SECTION HEADER ── */}
      <div className="mb-3">
        <span className="text-[11px] font-medium uppercase tracking-wider text-text-sec">
          {isEnglish ? 'Workflows & Documents' : 'Alur Kerja & Dokumen'}
        </span>
      </div>

      {/* ── FEATURED FULL-WIDTH CARD: DRAFT DOKUMEN PERPAJAKAN (.DOCX) ── */}
      <div
        onClick={() => handleTopicClick(featuredDraftingTopic)}
        className="gold-card cursor-pointer p-4 sm:p-5 mb-3.5 group"
      >
        <div className="flex items-start justify-between gap-3 mb-3.5">
          <div className="flex items-start gap-3">
            <FileText size={18} className="text-text-sec group-hover:text-text-pri transition-colors mt-0.5 shrink-0" />
            <div>
              <h3 className="font-semibold text-[14px] text-text-pri">
                {featuredDraftingTopic.title}
              </h3>
              <p className="text-xs text-text-sec mt-0.5">
                {featuredDraftingTopic.desc}
              </p>
            </div>
          </div>

          {loadingTopic === featuredDraftingTopic.title ? (
            <Loader2 size={14} className="animate-spin text-text-sec shrink-0" />
          ) : (
            <ArrowUpRight size={15} className="text-muted group-hover:text-text-pri transition-colors shrink-0" />
          )}
        </div>

        {/* 6 Document Sub-Prompt Buttons */}
        <div
          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          {featuredDraftingTopic.subPrompts.map((sub, sIdx) => {
            const subTitle = `Draft ${sub.label}`;
            const isSubLoading = loadingTopic === subTitle;
            return (
              <button
                key={sIdx}
                type="button"
                onClick={() =>
                  handleTopicClick({
                    ...featuredDraftingTopic,
                    title: subTitle,
                    templatePrompt: sub.prompt,
                  })
                }
                className="
                  flex items-center justify-between text-left px-3 py-2 rounded-lg
                  bg-surface-inset hover:bg-text-pri
                  text-xs font-medium text-text-pri hover:text-paper
                  group/btn transition-colors duration-150 cursor-pointer
                "
              >
                <span className="truncate">{sub.label}</span>
                {isSubLoading ? (
                  <Loader2 size={11} className="animate-spin shrink-0 ml-1" />
                ) : (
                  <ArrowUpRight
                    size={12}
                    className="opacity-40 group-hover/btn:opacity-100 shrink-0 ml-1 transition-opacity"
                  />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── SYMMETRICAL 6-CARD BENTO GRID ── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3.5 items-stretch">
        {consultTopics.map((topic, idx) => {
          const IconComp = topic.icon;
          const isLoadingThis = loadingTopic === topic.title;

          return (
            <div
              key={idx}
              onClick={() => handleTopicClick(topic)}
              className="gold-card cursor-pointer p-4 flex flex-col gap-2.5 hover:-translate-y-0.5 group"
            >
              <div className="flex items-center justify-between">
                <IconComp
                  size={17}
                  className="text-text-sec group-hover:text-text-pri transition-colors shrink-0"
                />
                {isLoadingThis ? (
                  <Loader2 size={14} className="animate-spin text-text-sec" />
                ) : (
                  <ArrowUpRight
                    size={15}
                    className="text-muted group-hover:text-text-pri transition-colors shrink-0"
                  />
                )}
              </div>

              <div>
                <h3 className="font-semibold text-[13.5px] text-text-pri mb-1">
                  {topic.title}
                </h3>
                <p className="text-xs text-text-sec leading-relaxed line-clamp-2">
                  {topic.desc}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
